import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Typography } from '~/components/elements/Typography'
import { ConditionOperationSelector } from '~/components/features/ConditionOperationSelector'
import { SelectorListContainer } from '~/components/features/SelectorListContainer'
import {
  SurveyGroupCustomerAttributeGroupSelector,
  type SurveyGroupCustomerAttributeGroupSelectorProps,
} from '~/components/features/SurveyGroupCustomerAttributeGroupSelector'
import { useFormField } from '~/hooks/useFormField'
import { compact, findByPath, getByPath } from '~/utils'

import {
  type InputValues,
  type SurveyGroupCustomerAttributeGroup,
} from '../utils'

import { CustomerAttributesInput } from './CustomerAttributesInput'

export type CustomerAttributeGroupInputProps = {
  control: Control<InputValues>
  customerAttributeGroups: SurveyGroupCustomerAttributeGroup[]
  filterGroupIndex: number
  filterIndex: number
}

export const CustomerAttributeGroupInput = ({
  control,
  customerAttributeGroups,
  filterGroupIndex,
  filterIndex,
}: CustomerAttributeGroupInputProps) => {
  const { t } = useTranslation()
  const namePrefix =
    `filterGroups.${filterGroupIndex}.${filterIndex}.customerAttribute` as const
  const customerAttributeGroupIdField = useFormField(
    control,
    `${namePrefix}.surveyGroupCustomerAttributeGroupId`,
  )
  const operationField = useFormField(control, `${namePrefix}.operation`)
  const customerAttributesField = useFormField(
    control,
    `${namePrefix}.customerAttributes`,
  )

  const handleCustomerAttributeGroupChange = React.useCallback<
    EH<
      SurveyGroupCustomerAttributeGroupSelectorProps<SurveyGroupCustomerAttributeGroup>,
      'onChange'
    >
  >(
    (customerAttributeGroupId) => {
      customerAttributeGroupIdField.onChange(customerAttributeGroupId)

      const customerAttributeGroup = findByPath(
        customerAttributeGroups,
        'id',
        customerAttributeGroupId,
      )
      const customerAttribute = getByPath(
        customerAttributeGroup,
        'customerAttributes.0',
      )
      const customerAttributes = compact([customerAttribute])
      customerAttributesField.onChange(customerAttributes)
    },
    [
      customerAttributeGroupIdField,
      customerAttributeGroups,
      customerAttributesField,
    ],
  )

  const customerAttributes = React.useMemo(() => {
    const customerAttributeGroup = findByPath(
      customerAttributeGroups,
      'id',
      customerAttributeGroupIdField.value,
    )

    return customerAttributeGroup?.customerAttributes ?? []
  }, [customerAttributeGroupIdField.value, customerAttributeGroups])

  return (
    <SelectorListContainer>
      <Typography fontWeight={700} variant="heading3">
        {t('customized_segments_group_5')}
      </Typography>

      <SurveyGroupCustomerAttributeGroupSelector
        customerAttributeGroups={customerAttributeGroups}
        {...customerAttributeGroupIdField}
        onChange={handleCustomerAttributeGroupChange}
      />

      <Typography fontWeight={700} variant="heading3">
        {t('condition_2')}
      </Typography>

      <ConditionOperationSelector {...operationField} />

      <Typography fontWeight={700} variant="heading3">
        {t('attribute_1')}
      </Typography>

      <CustomerAttributesInput
        conditionOperation={operationField.value}
        customerAttributes={customerAttributes}
        {...customerAttributesField}
      />
    </SelectorListContainer>
  )
}
